import {
  AccountProductEnum,
  InternalCheckingAccount,
  InternalSavingsAccount,
  PinwheelExperienceInput,
} from '@varo/graphql-schema-types';
import { setStringAsync } from 'expo-clipboard';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import myAccountsQuery from 'services/graphql/queries/MoveMoney/GetMyAccounts';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { useDirectDepositLoadingComponent } from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

interface MyAccountsResponse {
  myAccounts: {
    internal: (InternalCheckingAccount | InternalSavingsAccount)[];
  };
  fetching: boolean;
  error: {
    message: string;
  };
}

const snackbarDuration = 3000;

const AccountNumbers = () => {
  const router = useRouter();
  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const { refetchDdManagementFlow, handleDdManagementLinkTokenFailure } =
    useDirectDepositManagementFlow();

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'manageAccounts',
  });

  const [showAccountNumbers, setShowAccountNumbers] = useState(false);
  const [showSnackBar, setShowSnackBar] = useState(false);

  const [{ data, fetching: loading, error }, refetchMyAccountsQuery] =
    useQuery<MyAccountsResponse>({
      query: myAccountsQuery,
    });
  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  const internalAccounts =
    data?.myAccounts?.internal.filter((account) => account.id) ?? [];

  const hasMoreThanOneAccount = internalAccounts.length > 1;

  const refresh = () => {
    refetchMyAccountsQuery({ requestPolicy: 'network-only' });
  };

  const cancel = () => {
    router.back();
  };

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(error);

  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: refresh,
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel = error
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: responseError?.title ?? defaultTitle,
        messageBody: responseError?.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const handleCopyToClipboard = useCallback((text: string) => {
    void setStringAsync(text);
    setShowSnackBar(true);
  }, []);

  useAutoHideSnackbar(
    Boolean(showSnackBar),
    () => {
      setShowSnackBar(false);
    },
    snackbarDuration,
  );

  const handleSwitchToggle = () => setShowAccountNumbers((prev) => !prev);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('accountNumbers.title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loading || isPinwheelLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={loading}
            isPinwheelLoading={isPinwheelLoading}
          />
        }
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
      {internalAccounts.map(
        (account: InternalCheckingAccount | InternalSavingsAccount) => (
          <View key={account.id}>
            <SectionDivider
              labelText={translatedText(
                account?.product === AccountProductEnum.Checking
                  ? 'accountNumbers.checkingAccountHeading'
                  : 'accountNumbers.savingsAccountHeading',
              )}
            />
            <ListItem
              primaryCopy={
                showAccountNumbers
                  ? (account?.fullAccountNumber as string)
                  : (account?.accountNumber as string)
              }
              secondaryCopy={translatedText('accountNumbers.accountNumber')}
              lineFormat={ListItemLineFormat.LABEL}
              showsChevron={false}
              trailingAccessory={{
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'COPY',
                  iconAccessibilityLabel: 'accountNumber-copy-icon',
                },
              }}
              onPress={() =>
                handleCopyToClipboard(account?.fullAccountNumber as string)
              }
            />
            <View style={styles.hairLineDivider}>
              <HairLineDivider />
            </View>
            <ListItem
              primaryCopy={account?.routingNumber as string}
              secondaryCopy={translatedText('accountNumbers.routingNumber')}
              lineFormat={ListItemLineFormat.LABEL}
              showsChevron={false}
              trailingAccessory={{
                id: '1',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'COPY',
                  iconAccessibilityLabel: 'routingNumber-copy-icon',
                },
              }}
              onPress={() =>
                handleCopyToClipboard(account?.routingNumber as string)
              }
            />
            {account?.product === AccountProductEnum.Checking ? (
              <View style={styles.setUpDirectDeposit}>
                <Button
                  label={translatedText('accountNumbers.manageDirectDeposit')}
                  type="SECONDARY"
                  onPress={() => {
                    initiatePinwheel({
                      input: {
                        experience: PinwheelExperienceInput.DirectDepositSwitch,
                        skipIntroScreen,
                      },
                      launchBehavior: skipIntroScreen
                        ? { delayAfterTokenMs: 3000 }
                        : undefined,
                      onOutcome: refetchDdManagementFlow,
                      onLinkTokenError: handleDdManagementLinkTokenFailure,
                    });
                  }}
                  analyticsId="directDepositFlowInitiated"
                  customAttributes={{
                    cta: 'Manage direct deposit',
                  }}
                />
              </View>
            ) : null}
            <View style={styles.sectionGap}></View>
          </View>
        ),
      )}
      <View style={styles.showAccountNumbers}>
        <ListItem
          primaryCopy={translatedText(
            hasMoreThanOneAccount
              ? 'accountNumbers.showAccountNumbers'
              : 'accountNumbers.showAccountNumber',
          )}
          showsChevron={false}
          trailingAccessory={{
            id: '1',
            name: VXLComponentId.SWITCH,
            props: {
              isOn: showAccountNumbers,
              onValueChange: handleSwitchToggle,
            },
          }}
        />
      </View>
      {showSnackBar ? (
        <Snackbar
          text={translatedText('accountNumbers.copyToClipBoard')}
          duration={snackbarDuration}
          buttonOnPress={() => setShowSnackBar(false)}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexGrow: 1,
  },
  hairLineDivider: {
    paddingHorizontal: theme.spacing.m,
  },
  sectionGap: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  setUpDirectDeposit: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  showAccountNumbers: {
    flex: 1,
  },
});

export default AccountNumbers;
